import { parsePhoneNumber } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import styled from 'styled-components';

import {
  CallButton,
  UrlButton,
  WhatsAppButtonDefinition,
  WhatsAppButtonType,
  WhatsAppContentDefinition,
} from 'modules/graphql/types';
import { ExpandableChevron } from 'modules/templateEditor/common';
import { WhatsAppRestrictionText } from 'modules/templateEditor/common/RestrictionText';
import {
  ButtonController,
  ButtonTypeVariationContainer,
  ButtonTypeVariationHeaderContainer,
  DeleteIconWrapper,
  InputWrapper,
} from 'modules/templateEditor/common/styles';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { BUTTON_VARIANTS } from 'ui/button';
import { FormField } from 'ui/formField';
import { Delete, Phone24, Reply24, Touchpad24 } from 'ui/icons';
import { InputText } from 'ui/inputs';
import { PhoneNumberCountry } from 'ui/phoneNumberCountry';
import { RadioGroup } from 'ui/radioGroup/radioGroup';
import { Option, Orientation } from 'ui/radioGroup/types';
import { spacing } from 'ui/theme';
import {
  MAX_BUTTON_TITLE_LENGTH,
  MAX_BUTTON_URL_LENGTH,
  SETTINGS_DISPLAY_CONTENT,
} from '../constants';
import { EditableTitle } from '../EditableTitle';

const StyledFormField = styled(FormField)`
  margin-top: 0 !important;
`;

const WhatsAppButtonTypeVariationContainer = styled(ButtonTypeVariationContainer)`
  gap: ${spacing.threeQuarter};
  width: 100%;
`;

const ButtonActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const PlaceholderInputWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  position: relative;
  width: 100%;
`;

const StyledInputText = styled(InputText)`
  padding-right: ${spacing.doubleHalf};
`;

const IconWrapper = styled.div`
  padding-top: ${spacing.base};
`;

const ButtonOptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StyledRadioGroup = styled(RadioGroup)`
  .RGItemContainer {
    background-color: white;
  }`;

const MAX_NUMBER_OF_URL_BUTTONS = 2;
const MAX_NUMBER_OF_CALL_BUTTONS = 1;

type Props = {
  value: WhatsAppButtonDefinition;
  index: number;
  draggableProvided: DraggableProvided;
  buttonCounts: Record<string, number>;
  errorArr: any[];
};

enum INPUT_FIELDS {
  BUTTON_TEXT = 'buttonText',
  PHONE_NUMBER = 'phoneNumber',
  INPUT_PLACEHOLDER = 'inputPlaceholder',
  INPUT_TITLE = 'inputTitle',
  IDENTIFIER = 'identifier',
  PAYLOAD = 'payload',
  URL = 'url',
}

const DEFAULT_FIELDS = {
  [WhatsAppButtonType.urlButton]: {
    url: '',
  },
  [WhatsAppButtonType.callButton]: {
    phoneNumber: '',
  },
};

export const ButtonTypeVariations = ({
  value,
  index,
  draggableProvided,
  errorArr,
  buttonCounts,
}: Props) => {
  const { liveTemplate, setCurrentMediumField } = useEditableFieldsContext();

  const [currButtonType, setCurrButtonType] = useState(WhatsAppButtonType.urlButton);
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState<string | undefined>();
  const whatsAppMediumData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const buttonData = (whatsAppMediumData?.buttons || []) as WhatsAppButtonDefinition[];
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const { ids, labels, helpText } = SETTINGS_DISPLAY_CONTENT.buttons;

  const buttonErrors = errorArr[index];

  const isUrlButtonDisabled =
    buttonCounts.url >= MAX_NUMBER_OF_URL_BUTTONS &&
    currButtonType !== WhatsAppButtonType.urlButton;
  const isCallButtonDisabled =
    buttonCounts.phoneNumber >= MAX_NUMBER_OF_CALL_BUTTONS &&
    currButtonType !== WhatsAppButtonType.callButton;

  useEffect(() => {
    // @ts-ignore
    if (typeof value?.url === 'string') {
      setCurrButtonType(WhatsAppButtonType.urlButton);
      // @ts-ignore
    } else if (typeof value?.phoneNumber === 'string') {
      setCurrButtonType(WhatsAppButtonType.callButton);
    } else {
      setCurrButtonType(WhatsAppButtonType.quickReplyButton);
    }
  }, [value]);

  useEffect(() => {
    if (currButtonType === WhatsAppButtonType.callButton) {
      try {
        const parsedPhoneNumber = parsePhoneNumber((value as CallButton)?.phoneNumber ?? '');
        setSelectedPhoneCountry(parsedPhoneNumber.country);
      } catch {
        // @ts-ignore
      }
    }
  }, [currButtonType]);

  const ButtonTypeOptions: Option[] = [
    {
      id: `${WhatsAppButtonType.urlButton}-${index}`,
      label: (
        <ButtonOptionWrapper>
          <IconWrapper>
            <Touchpad24 />
          </IconWrapper>
          Visit website
        </ButtonOptionWrapper>
      ),
      value: WhatsAppButtonType.urlButton,
      isDisabled: isUrlButtonDisabled,
      isTooltipEnabled: isUrlButtonDisabled,
      tooltipContent: 'Max of 2 visit website buttons',
    },
    {
      id: `${WhatsAppButtonType.callButton}-${index}`,
      label: (
        <ButtonOptionWrapper>
          <IconWrapper>
            <Phone24 />
          </IconWrapper>
          Call phone
        </ButtonOptionWrapper>
      ),
      value: WhatsAppButtonType.callButton,
      isDisabled: isCallButtonDisabled,
      isTooltipEnabled: isCallButtonDisabled,
      tooltipContent: 'Max of 1 call button',
    },
    {
      id: `${WhatsAppButtonType.quickReplyButton}-${index}`,
      label: (
        <ButtonOptionWrapper>
          <IconWrapper>
            <Reply24 />
          </IconWrapper>
          Quick reply
        </ButtonOptionWrapper>
      ),
      value: WhatsAppButtonType.quickReplyButton,
    },
  ];

  const onChangeUpdateInputText = (field: string, newText: string, additionalField?: string) => {
    const newButton = {
      ...value,
      [field]: newText,
      ...(additionalField ? { [additionalField]: newText } : {}),
    };
    buttonData[index] = newButton;
    setCurrentMediumField('buttons', buttonData);
  };

  /** On change button type, use initial button state
   * since we don't want the previous data to carry over to new button type.
   * */
  const onChangeButtonType = (buttonType: WhatsAppButtonType) => {
    const newButton = {
      buttonText: buttonData[index]?.buttonText, // preserve previous data,
      title: buttonData[index]?.title,
      // @ts-ignore
      ...(DEFAULT_FIELDS[buttonType] || {}),
    };
    buttonData[index] = newButton;
    setCurrButtonType(buttonType);
    setCurrentMediumField('buttons', buttonData);
  };

  return (
    <WhatsAppButtonTypeVariationContainer data-test="button-tile">
      <ButtonTypeVariationHeaderContainer isExpanded={false}>
        <EditableTitle data={value} draggableProvided={draggableProvided} />
        <ButtonController data-test="button-header-ctrl">
          <DeleteIconWrapper
            onClick={() => {
              buttonData.splice(index, 1);
              setCurrentMediumField('buttons', buttonData);
            }}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <Delete width={16} height={16} />
          </DeleteIconWrapper>
          <ExpandableChevron
            isExpanded={isExpanded}
            onClick={(result: boolean) => setIsExpanded(result)}
          />
        </ButtonController>
      </ButtonTypeVariationHeaderContainer>

      {isExpanded && (
        <InputWrapper gap>
          <div>
            <FormField isHorizontalLayout>
              <StyledRadioGroup
                orientation={Orientation.HORIZONTAL}
                isBordered
                name={`${buttonData[index]?.buttonText}-${index}`}
                onSelect={(selectedType) => {
                  const buttonType = selectedType as WhatsAppButtonType;
                  onChangeButtonType(buttonType);
                }}
                alignRight
                isOverlapped
                value={currButtonType}
                options={ButtonTypeOptions}
              />
            </FormField>
          </div>

          <ButtonActionWrapper>
            <StyledFormField
              labelText={labels.buttonText}
              hasError={buttonErrors?.buttonTextErrors.length > 0}
              errorText={buttonErrors?.buttonTextErrors.join(', ')}
              helpText={!buttonErrors?.buttonTextErrors.length && helpText.buttonText}
              restrictionText={
                <WhatsAppRestrictionText
                  textContent={value?.buttonText || ''}
                  maxChars={MAX_BUTTON_TITLE_LENGTH}
                />
              }
            >
              <PlaceholderInputWrapper>
                <StyledInputText
                  hasError={buttonErrors?.buttonTextErrors.length > 0}
                  id={ids.buttonTextFieldId}
                  onChange={(newText) =>
                    onChangeUpdateInputText(INPUT_FIELDS.BUTTON_TEXT, newText, 'title')
                  }
                  // @ts-ignore
                  value={value?.buttonText || ''}
                  width={'100%'}
                  maxLength={MAX_BUTTON_TITLE_LENGTH}
                />
              </PlaceholderInputWrapper>
            </StyledFormField>
            {currButtonType === WhatsAppButtonType.urlButton && (
              <FormField
                labelText={labels.url}
                hasError={buttonErrors?.buttonUrlErrors.length > 0}
                errorText={buttonErrors?.buttonUrlErrors.join(', ')}
                helpText={!buttonErrors?.buttonUrlErrors.length && helpText.url}
                restrictionText={
                  <WhatsAppRestrictionText
                    textContent={(value as UrlButton)?.url || ''}
                    maxChars={MAX_BUTTON_URL_LENGTH}
                  />
                }
              >
                <PlaceholderInputWrapper>
                  <StyledInputText
                    id={ids.urlFieldId}
                    hasError={buttonErrors?.buttonUrlErrors.length > 0}
                    onChange={(newText: string) =>
                      onChangeUpdateInputText(INPUT_FIELDS.URL, newText)
                    }
                    value={(value as UrlButton)?.url || ''}
                    width={'100%'}
                    maxLength={MAX_BUTTON_URL_LENGTH}
                  />
                </PlaceholderInputWrapper>
              </FormField>
            )}
            {currButtonType === WhatsAppButtonType.callButton && (
              <StyledFormField
                labelText={labels.phoneNumber}
                hasError={buttonErrors?.buttonPhoneErrors.length > 0}
                errorText={buttonErrors?.buttonPhoneErrors.join(', ')}
              >
                <PlaceholderInputWrapper>
                  <PhoneNumberCountry
                    id={ids.phoneFieldId}
                    supportedCountries={[]}
                    onPhoneChange={(phoneNumber) =>
                      onChangeUpdateInputText(INPUT_FIELDS.PHONE_NUMBER, phoneNumber)
                    }
                    phoneValue={(value as CallButton)?.phoneNumber || ''}
                    countryValue={selectedPhoneCountry}
                    onCountrySelect={(country) => setSelectedPhoneCountry(country)}
                    width={'100%'}
                    hasError={buttonErrors?.buttonPhoneErrors.length > 0}
                  />
                </PlaceholderInputWrapper>
              </StyledFormField>
            )}
          </ButtonActionWrapper>
        </InputWrapper>
      )}
    </WhatsAppButtonTypeVariationContainer>
  );
};
